import { FeatureLimitations } from '../model/feature-permissions.enum';
import { DistinctType, ISODateString, ObjectUUId } from './common.dto';

export type SubscriptionId = ObjectUUId<'SubscriptionId'>;
export type ExternalSubscriptionId = DistinctType<string, 'ExternalSubscriptionId'>;
export type ExternalCustomerId = DistinctType<string, 'ExternalCustomerId'>;

export enum Currency {
  EUR = 'EUR',
}

export interface SubscriptionDTO {
  id: SubscriptionId;

  source: SubscriptionSource;

  status: SubscriptionStatus;

  type: SubscriptionType;

  period: SubscriptionPeriod;

  periodEnd: ISODateString | undefined;

  expiration: ISODateString | undefined;
}

export enum SubscriptionFamily {
  Free = 'free',
  Base = 'base',
  Enterprise = 'enterprise',
}

export enum SubscriptionType {
  Free = 'free',

  Base_500 = 'base-500',
  Base_1k = 'base-1k',
  Base_3k = 'base-2k',
  Base_5k = 'base-5k',
  Base_10k = 'base-10k',

  Enterprise = 'enterprise',
}

export enum SubscriptionPeriod {
  Monthly = 'monthly',

  Annual = 'annual',
}

export enum SubscriptionSource {
  LemonSqueezy = 'lemon-squeezy',

  Custom = 'custom',
}

export enum SubscriptionStatus {
  Active = 'active',

  Cancelled = 'cancelled',

  Expired = 'expired',

  Trialing = 'trialing',
}

export const ACTIVE_SUBSCRIPTION_STATUSES = new Set([
  SubscriptionStatus.Active,
  SubscriptionStatus.Trialing,
]);

export const SUBSCRIPTION_LIMITATIONS: Readonly<{
  readonly [subType in SubscriptionType]: FeatureLimitations;
}> = {
  [SubscriptionType.Free]: {
    monthlyActiveUsers: 100,
    maxKnowledgeBaseCount: 1,
    maxKnowledgeBaseDomainCount: 1,
    maxArticleCount: 10,
  },

  [SubscriptionType.Base_500]: {
    monthlyActiveUsers: 500,
    maxKnowledgeBaseCount: 5,
    maxKnowledgeBaseDomainCount: 5,
    maxArticleCount: 1000,
  },

  [SubscriptionType.Base_1k]: {
    monthlyActiveUsers: 1000,
    maxKnowledgeBaseCount: 5,
    maxKnowledgeBaseDomainCount: 5,
    maxArticleCount: 1000,
  },

  [SubscriptionType.Base_3k]: {
    monthlyActiveUsers: 3000,
    maxKnowledgeBaseCount: 5,
    maxKnowledgeBaseDomainCount: 5,
    maxArticleCount: 1000,
  },

  [SubscriptionType.Base_5k]: {
    monthlyActiveUsers: 5000,
    maxKnowledgeBaseCount: 5,
    maxKnowledgeBaseDomainCount: 5,
    maxArticleCount: 1000,
  },

  [SubscriptionType.Base_10k]: {
    monthlyActiveUsers: 10000,
    maxKnowledgeBaseCount: 5,
    maxKnowledgeBaseDomainCount: 5,
    maxArticleCount: 1000,
  },

  [SubscriptionType.Enterprise]: {
    monthlyActiveUsers: 200000,
    maxKnowledgeBaseCount: 5000,
    maxKnowledgeBaseDomainCount: 100,
    maxArticleCount: 10000,
  },
};

export const SUBSCRIPTION_CHECKOUT_DATA: Readonly<{
  readonly [subType in SubscriptionType]: {
    readonly [period in SubscriptionPeriod]: {
      name: string;
      LSProductId: string;
      LSCheckoutId: string;
      price: {
        [currency in Currency]: number;
      }
    }
  }
}> = {
  [SubscriptionType.Free]: {
    [SubscriptionPeriod.Monthly]: {
      name: 'Free',
      LSProductId: '',
      LSCheckoutId: '',
      price: {
        [Currency.EUR]: 0,
      },
    },
    [SubscriptionPeriod.Annual]: {
      name: 'Free',
      LSProductId: '',
      LSCheckoutId: '',
      price: {
        [Currency.EUR]: 0,
      },
    },
  },
  [SubscriptionType.Base_500]: {
    [SubscriptionPeriod.Monthly]: {
      name: 'Base 500',
      LSProductId: '574795',
      LSCheckoutId: 'baf1da4e-c9f0-4ab4-8429-244483165f70',
      price: {
        [Currency.EUR]: 25,
      },
    },
    [SubscriptionPeriod.Annual]: {
      name: 'Base 500',
      LSProductId: '574794',
      LSCheckoutId: '90385485-ede5-49de-a82d-7021f6947fc8',
      price: {
        [Currency.EUR]: 240 / 12,
      },
    },
  },
  [SubscriptionType.Base_1k]: {
    [SubscriptionPeriod.Monthly]: {
      name: 'Base 1k',
      LSProductId: '574792',
      LSCheckoutId: '12fcbbe7-86d1-468f-9bcc-aacfbca2ccc8',
      price: {
        [Currency.EUR]: 49,
      },
    },
    [SubscriptionPeriod.Annual]: {
      name: 'Base 1k',
      LSProductId: '574791',
      LSCheckoutId: '8e2acef5-fe9d-4ac1-8d3d-37de5a28d067',
      price: {
        [Currency.EUR]: 490 / 12,
      },
    },
  },
  [SubscriptionType.Base_3k]: {
    [SubscriptionPeriod.Monthly]: {
      name: 'Base 3k',
      LSProductId: '574803',
      LSCheckoutId: '2cd9f638-4f5e-4c1e-8516-3a0f9ab7e079',
      price: {
        [Currency.EUR]: 69,
      },
    },
    [SubscriptionPeriod.Annual]: {
      name: 'Base 3k',
      LSProductId: '574793',
      LSCheckoutId: 'db66dc26-e66f-4cd6-90f3-78b5a2280349',
      price: {
        [Currency.EUR]: 659 / 12,
      },
    },
  },
  [SubscriptionType.Base_5k]: {
    [SubscriptionPeriod.Monthly]: {
      name: 'Base 5k',
      LSProductId: '574797',
      LSCheckoutId: 'b3a15850-259e-44bb-9412-82b15d0d9576',
      price: {
        [Currency.EUR]: 99,
      },
    },
    [SubscriptionPeriod.Annual]: {
      name: 'Base 5k',
      LSProductId: '574796',
      LSCheckoutId: 'b5387be2-ca07-4cfc-80e8-535cc776b671',
      price: {
        [Currency.EUR]: 949 / 12,
      },
    },
  },
  [SubscriptionType.Base_10k]: {
    [SubscriptionPeriod.Monthly]: {
      name: 'Base 10k',
      LSProductId: '574790',
      LSCheckoutId: '6fe965c7-f546-4522-93ca-0fb3f47573b3',
      price: {
        [Currency.EUR]: 145,
      },
    },
    [SubscriptionPeriod.Annual]: {
      name: 'Base 10k',
      LSProductId: '574789',
      LSCheckoutId: '3c40d085-18b8-4446-ac87-0d36e1069c53',
      price: {
        [Currency.EUR]: 1392 / 12,
      },
    },
  },
  [SubscriptionType.Enterprise]: {
    [SubscriptionPeriod.Monthly]: {
      name: 'Enterprise',
      LSProductId: '',
      LSCheckoutId: '',
      price: {
        [Currency.EUR]: 0,
      },
    },
    [SubscriptionPeriod.Annual]: {
      name: 'Enterprise',
      LSProductId: '',
      LSCheckoutId: '',
      price: {
        [Currency.EUR]: 0,
      },
    },
  },
};

export const LS_PRODUCT_SUBSCRIPTION_INDEX: ReadonlyMap<string, {
  type: SubscriptionType;
  period: SubscriptionPeriod;
}> = new Map(Object.entries(SUBSCRIPTION_CHECKOUT_DATA).flatMap(([subscriptionType, data]) => Object.entries(data).map(([period, _]) => {
  return [_.LSProductId, {
    type: <SubscriptionType>subscriptionType,
    period: <SubscriptionPeriod>period,
  }];
})));